import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRebaseClient } from "@rebasepro/app";
import { useSidePanel } from "@rebasepro/cms";
import type { RebaseClient } from "@rebasepro/types";
import type { EntityCustomViewParams } from "@rebasepro/cms-types";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    CircularProgressCenter as RebaseCircularProgressCenter,
    FilterChip as RebaseFilterChip,
    IconButton as RebaseIconButton,
    SearchBar as RebaseSearchBar,
    Tooltip as RebaseTooltip,
    Typography as RebaseTypography,
    RefreshCwIcon as RebaseRefreshCwIcon,
    cls,
    cardMixin,
    defaultBorderMixin,
    surfaceHoverMixin,
    iconSize
} from "@rebasepro/ui";
import {
    ChevronLeft as RebaseChevronLeftIcon,
    ChevronRight as RebaseChevronRightIcon,
    ExternalLink as RebaseExternalLinkIcon,
    FileText as RebaseFileTextIcon,
    Link2 as RebaseLink2Icon
} from "lucide-react";
import { PersonAvatar } from "./_avatar";
import { AREA_LABELS, LEVEL_COLORS, summarizeTalent, type TalentSummary } from "./_talent_taxonomy";
import { credentialLabel } from "../taxonomy/credentials";
import { FIT_ORDER_RULES, applicantFit, compareFit, vacancyAsk, type ApplicantFit } from "../taxonomy/fit";
import { usePrivateFileOpener } from "./_private_file";
import { useAnnouncedSnackbar } from "./_workspace";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const CircularProgressCenter = RebaseCircularProgressCenter as React.ComponentType<any>;
const FilterChip = RebaseFilterChip as React.ComponentType<any>;
const IconButton = RebaseIconButton as React.ComponentType<any>;
const SearchBar = RebaseSearchBar as React.ComponentType<any>;
const Tooltip = RebaseTooltip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const RefreshCwIcon = RebaseRefreshCwIcon as React.ComponentType<any>;
const ChevronLeftIcon = RebaseChevronLeftIcon as React.ComponentType<any>;
const ChevronRightIcon = RebaseChevronRightIcon as React.ComponentType<any>;
const ExternalLinkIcon = RebaseExternalLinkIcon as React.ComponentType<any>;
const FileTextIcon = RebaseFileTextIcon as React.ComponentType<any>;
const Link2Icon = RebaseLink2Icon as React.ComponentType<any>;

/**
 * The `talent_applications.status` enum, which is the real record of what the
 * team decided — as opposed to the parallel shortlist table the deleted
 * matching feature kept. Advancing one of these is a fact about a person who
 * applied; it is not an opinion about someone who did not.
 */
type ApplicationStatus = "applied" | "reviewing" | "interview" | "rejected" | "hired";

const STATUS_CHIP: Record<ApplicationStatus, { label: string; color?: string }> = {
    applied: { label: "Sin revisar", color: "blue" },
    reviewing: { label: "En revisión", color: "orange" },
    interview: { label: "Entrevista", color: "purple" },
    // Feminine, like every other name for a state in this panel: the thing
    // being labelled is *la postulación*, and it is labelled in three places —
    // the chip, the tab and the button — which have to say the same word.
    rejected: { label: "Descartada" },
    hired: { label: "Contratada", color: "green" }
};

/**
 * The whole pipeline, offered as buttons, in the order a decision moves.
 *
 * `applied` is in the list and used to not be, which made every other button a
 * one-way door: a mis-clicked "En revisión" could not be taken back from the
 * card that caused it. Being able to put an application back where it was is
 * the difference between a control somebody tries and one they avoid.
 */
const STATUS_ACTIONS: ApplicationStatus[] = ["applied", "reviewing", "interview", "hired", "rejected"];

/**
 * What each button does, spelled out.
 *
 * These buttons were verbs once — "Empezar a revisarla", "Marcar como
 * contratada" — to keep them from reading identically to the state chip on the
 * same card, where "Entrevista" would have meant both "she is at this stage"
 * and "move her to it". The verbs fixed the ambiguity and bought a worse
 * problem: "Empezar a revisarla" does not say what it changes, so nobody could
 * tell whether it opened something, wrote to somebody, or moved a row.
 *
 * The verb is now said once, in front of the row — "Mover a" — and the buttons
 * are the state names, matching the chip and the tabs exactly. One word means
 * one thing in all three places, and the row reads as the sentence it is.
 * What each move actually commits you to is on the tooltip.
 *
 * Including who hears about it. Every move but back to "Sin revisar" sends the
 * candidate a letter once the change is saved (drizzle/0065, and the backend's
 * `application-status-notice.ts`), whichever door made it. "En revisión" used
 * to say "No le escribe nada a nadie", which was true until it was not — and a
 * tooltip that promises silence is the one people rely on.
 */
const STATUS_MOVE_HINT: Record<ApplicationStatus, string> = {
    applied: "Devuélvela al montón de sin revisar, como si nadie la hubiera abierto. A la persona no le escribimos.",
    reviewing: "Sácala de sin revisar: alguien la está leyendo. Le escribimos para contárselo.",
    interview: "Habéis quedado en entrevistarla. Le escribimos que la empresa quiere hablar con ella y que se pondrá en contacto.",
    hired: "Se queda con el puesto. Le escribimos para darle la enhorabuena.",
    rejected: "No sigue adelante. Se queda en la lista, marcada, para no volver a abrirla. Le escribimos para decírselo."
};

/**
 * The tabs, which are the pipeline read left to right.
 *
 * Same idiom as Correo and Empresas: a chip per state with its count on it, and
 * one line underneath saying what the open one holds. The counts are the reason
 * the whole set is read before anything is drawn — a tab that cannot say how
 * many are behind it is a tab nobody clicks.
 *
 * "Contratadas" and "Descartadas" rather than the singular the chip on a card
 * uses: the chip labels one person, the tab labels a pile.
 */
type StatusFilter = "all" | ApplicationStatus;

const STATUS_TABS: { id: StatusFilter; label: string }[] = [
    { id: "all", label: "Todas" },
    { id: "applied", label: "Sin revisar" },
    { id: "reviewing", label: "En revisión" },
    { id: "interview", label: "Entrevista" },
    { id: "hired", label: "Contratadas" },
    { id: "rejected", label: "Descartadas" }
];

/**
 * What each tab holds, in one line, under the chips.
 *
 * Two words on a chip cannot distinguish "nobody has looked at these" from
 * "somebody is looking at these right now", and that distinction is the whole
 * difference between the first tab and the second.
 */
const STATUS_TAB_HINT: Record<StatusFilter, string> = {
    all: "Todo el mundo que se postuló. Lo que está sin decidir arriba, lo ya resuelto al final.",
    applied: "Nadie las ha abierto todavía. Aquí es donde empieza el trabajo.",
    reviewing: "Alguien las está mirando ahora mismo.",
    interview: "Han pasado a entrevista.",
    hired: "Se quedaron con el puesto.",
    rejected: "Descartadas. Siguen en la lista para no volver a mirarlas dos veces."
};

/**
 * How the list is ordered.
 *
 * "Encaje" is the panel's own composite — see {@link byFit} — and the default,
 * because the reason to open this tab is to find out who is worth reading. The
 * other two exist for the two questions the composite cannot answer: "who acaba
 * de postularse" and "dónde está fulanita".
 */
type SortMode = "fit" | "recent" | "name";

const SORT_LABEL: Record<SortMode, string> = {
    fit: "encaje",
    recent: "más recientes",
    name: "nombre"
};

/**
 * What the chosen order actually did, in one line.
 *
 * "Encaje" is the one that needs saying. There is no score behind it and there
 * deliberately is not one — see the note on {@link ApplicantsPanel} — so the
 * word on the chip is the only thing telling the reader why one person is above
 * another, and on its own it tells them nothing. This is the tie-break chain
 * written out in the order it is applied: each rule only speaks when the ones
 * before it tie.
 */
const SORT_HINT: Record<SortMode, string> = {
    fit: "No hay puntuación: sin decidir primero, luego quien cumple los requisitos, luego nivel declarado y experiencia.",
    recent: "Quien se postuló más tarde, primero. Lo ya resuelto, al final.",
    name: "Alfabético, sin hundir lo ya resuelto: es el orden para encontrar a alguien concreto."
};

/**
 * The whole chain, for the chip that needs it.
 *
 * The line under the chips has to be short enough that somebody reads it, and
 * the honest answer to "how is this ordered" is six rules deep. The short
 * version is on screen; this one is a hover away, on the chip that raises the
 * question.
 */
const FIT_RULES = FIT_ORDER_RULES.map((rule, i) => `${i + 1}. ${rule}`).join("\n");

/** How many cards are on screen at once. */
const PER_PAGE = 25;

/**
 * Above this many applicants, a search box earns its place.
 *
 * Below it every card is a scroll away and the box is one more control between
 * the reader and the eight people they came to read.
 */
const SEARCHABLE_FROM = 8;

/**
 * How many applications one read asks for.
 *
 * Lower than the 1000 the talents walker uses (`_talents_paging.ts`), and
 * deliberately: every row here drags a whole talent row behind it via
 * `include: ["*"]` — the questionnaire, the free text, the credential arrays —
 * so a page of 1000 of these is a payload of a different order than a page of
 * 1000 bare rows.
 */
const PAGE_SIZE = 500;

/**
 * Where the walk stops regardless.
 *
 * The order below is derived over the whole set — undecided first, then depth
 * in this vacancy's areas — and an order computed over the first page is not
 * that order, so the panel reads every application before it sorts anything.
 * That is the right trade at the volumes this product sees and the wrong one at
 * some volume, so there is a ceiling; and reaching it says so on screen instead
 * of quietly ranking a slice. The Candidatos list rendered empty for a month
 * because a read that did not fit was swallowed. This one is not swallowed.
 */
const MAX_APPLICANTS = 5000;

interface Applicant {
    applicationId: string;
    talentId: string;
    fullName: string;
    /** `talents.photo_url` — a storage key, resolved by `PersonAvatar`. */
    photoKey: string | null;
    email: string | null;
    linkedinUrl: string | null;
    cvUrl: string | null;
    status: ApplicationStatus;
    appliedAt: string | null;
    summary: TalentSummary;
    /** Areas the vacancy asks for that this person claims, with their level. */
    matched: { key: string; label: string; level: string; levelLabel: string; rank: number }[];
    /** Areas the vacancy asks for that this person does not claim at all. */
    missing: { key: string; label: string }[];
    /** Of the vacancy's areas, how many are claimed at `advanced`. */
    depth: number;
    /** Required credentials this person holds, and the ones they do not. */
    heldRequired: string[];
    /**
     * Required credentials we read out of what they wrote elsewhere rather than
     * out of an answer they gave. Counts as having it; never shown as a claim
     * they made. See `taxonomy/requirements.ts` and drizzle/0041.
     */
    inferredRequired: string[];
    missingRequired: string[];
    /**
     * Required credentials the candidate was never asked about.
     *
     * Held apart from `missingRequired` because they are not the same claim and
     * only one of them is disqualifying. See `taxonomy/requirements.ts`: the
     * questionnaire's skills question is newer than every candidate in the
     * database, so folding these two together marked every applicant to every
     * vacancy as short of every skill it required.
     */
    unknownRequired: string[];
    /** Desired credentials they happen to hold. Never disqualifying. */
    heldDesired: string[];
    /**
     * The whole reading the fields above are flattened from, and what
     * {@link byFit} orders by. `taxonomy/fit.ts` — the employer dashboard on the
     * site reads applicants through the same module, so the two screens cannot
     * disagree about who meets what.
     */
    fit: ApplicantFit;
}

function formatDate(value?: string | null): string | null {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("es");
}

/** A decision already taken. Sinks in every order but the alphabetical one. */
const settled = (s: ApplicationStatus) => s === "rejected" || s === "hired";

/**
 * The panel's "encaje" order: `compareFit` in `taxonomy/fit.ts`, which spells
 * out the chain and why an unmet requirement is a gate and not a term in a
 * score. Rejected and hired sink outright; they are settled.
 */
function byFit(a: Applicant, b: Applicant): number {
    return compareFit(
        { settled: settled(a.status), fit: a.fit, appliedAt: a.appliedAt },
        { settled: settled(b.status), fit: b.fit, appliedAt: b.appliedAt }
    );
}

/**
 * Newest first — and settled applications still sink.
 *
 * "Quién acaba de postularse" is a question about work waiting to be done, so a
 * rejection from this morning does not belong above an undecided application
 * from yesterday.
 */
function byRecent(a: Applicant, b: Applicant): number {
    if (settled(a.status) !== settled(b.status)) return settled(a.status) ? 1 : -1;
    return (b.appliedAt ?? "").localeCompare(a.appliedAt ?? "");
}

/**
 * By name, and this is the one order that does not sink anything.
 *
 * It exists to answer "dónde está fulanita", and an alphabetical list with a
 * hole in it where the settled ones were moved to the bottom answers it worse
 * than no order at all.
 */
function byName(a: Applicant, b: Applicant): number {
    return a.fullName.localeCompare(b.fullName, "es", { sensitivity: "base" });
}

const COMPARATORS: Record<SortMode, (a: Applicant, b: Applicant) => number> = {
    fit: byFit,
    recent: byRecent,
    name: byName
};

/**
 * Every application to this vacancy, walked in pages.
 *
 * Same shape as `fetchAllTalents`, and for the same reason: the data plane
 * refuses a read above its ceiling rather than clamping it, so "give me all of
 * them" has to be spelled as a walk. Two stop conditions, because either alone
 * is wrong — a short page is the real end of the data, and the reported total
 * catches a server that ignores `offset` and would otherwise hand back a full
 * page forever.
 *
 * `truncated` is the third: it says the ceiling stopped the walk, which is a
 * fact the panel has to admit on screen because everything it prints below is
 * derived over "all of them".
 */
async function fetchAllApplications(
    data: any,
    jobId: string
): Promise<{ rows: any[]; truncated: boolean }> {
    const rows: any[] = [];

    for (let offset = 0; ; offset += PAGE_SIZE) {
        // `include: ["*"]` brings the talent row with each application, so the
        // whole panel is one walk rather than one request per applicant.
        const res = await data.collection("talent-applications").find({
            where: { job_id: ["==", jobId] },
            orderBy: ["created_at", "desc"],
            include: ["*"],
            limit: PAGE_SIZE,
            offset
        });

        const page: any[] = res?.data ?? [];
        rows.push(...page);

        if (page.length < PAGE_SIZE) break;
        if (rows.length >= MAX_APPLICANTS) return { rows, truncated: true };

        const total = typeof res?.meta?.total === "number" ? res.meta.total : null;
        if (total !== null && rows.length >= total) break;
    }

    return { rows, truncated: false };
}

/**
 * Who applied to this vacancy, and how well each one lines up with what it asks
 * for.
 *
 * This is the readable half of the deleted matching workspace, pointed at the
 * only population it is allowed to describe: people who actually applied. The
 * old panel ranked *every* candidate against a vacancy and wrote its verdicts to
 * `match_pipeline`, a table parallel to the real one — so a suggestion and an
 * application rendered identically and the panel manufactured relationships
 * nobody had agreed to. Here the list is exactly `talent_applications` for this
 * job, and the buttons advance that application's own `status`.
 *
 * There is no score. The vacancy names a handful of sustainability areas and the
 * candidate claims some at a level; the overlap is a set intersection, printed
 * as chips coloured by the level claimed. A single percentage would have implied
 * a precision that self-declared questionnaire answers do not carry, and the
 * previous panel needed a paragraph of disclaimer to walk that back.
 */
export function ApplicantsPanel({ entity, formContext }: EntityCustomViewParams) {
    const client = useRebaseClient<RebaseClient>();
    const sidePanel = useSidePanel();
    const { snackbar, liveRegion } = useAnnouncedSnackbar();
    const openPrivateFile = usePrivateFileOpener();

    /**
     * `cv_url` is a storage key, not a URL: exchanged for a signed link on
     * click (`_private_file.tsx`). As an `href` it was a relative path that
     * 404ed for every CV this panel ever showed.
     */
    const openCv = useCallback(async (value: string) => {
        const outcome = await openPrivateFile(value, "el CV");
        if (!outcome.ok) snackbar.open({ type: "error", message: outcome.message });
    }, [openPrivateFile, snackbar]);

    const rawId = entity?.id ?? formContext?.entityId;
    const jobId = rawId ? String(rawId) : null;

    /**
     * What the vacancy asks for: its areas (minus the catch-all) with the level
     * it wants in each, and its credentials as canonical ids. Read off the
     * entity in hand rather than refetched — this view is rendered inside the
     * job form.
     *
     * The `_canon` columns rather than the raw text: raw is what the employer
     * typed and a candidate's raw text is what *they* typed, and "ISO 14001"
     * against "Iso 9001, ISO 14001" is not a comparison any string operation
     * gets right. The canonical ids are derived precisely so this can be a set
     * intersection. See `taxonomy/credentials.ts` and `taxonomy/fit.ts`.
     */
    const ask = useMemo(() => vacancyAsk((entity?.values ?? {}) as Record<string, unknown>), [entity]);
    const jobAreas = ask.areas;
    const required = ask.required;
    const desired = ask.desired;
    const jobUsesCatchAll = ask.usesCatchAll;

    const [applicants, setApplicants] = useState<Applicant[] | null>(null);
    /** The ceiling stopped the walk: what is on screen is not all of them. */
    const [truncated, setTruncated] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState<string | null>(null);

    // ── The toolbar ──────────────────────────────────────────────────
    const [tab, setTab] = useState<StatusFilter>("all");
    const [search, setSearch] = useState("");
    const [onlyQualified, setOnlyQualified] = useState(false);
    const [sortMode, setSortMode] = useState<SortMode>("fit");
    const [page, setPage] = useState(0);
    /**
     * Remounts the search box, which owns its own text.
     *
     * `SearchBar` is uncontrolled — it reports what was typed and keeps it — so
     * clearing the term in state leaves the word sitting in the box, filtering
     * nothing and saying it is. Bumping the key is how "quitar filtros" empties
     * the field it is undoing.
     */
    const [searchKey, setSearchKey] = useState(0);

    /**
     * The order the chips currently ask for, readable from inside `load`.
     *
     * `load` sorts the rows it just read and has to sort them the way the chips
     * say — but naming `sortMode` in its dependencies would make every sort chip
     * refetch the whole vacancy, a network round trip to reorder an array that
     * is already in memory. A ref reads the value without becoming a reason to
     * re-run.
     */
    const sortRef = useRef<SortMode>(sortMode);
    sortRef.current = sortMode;

    /** The scrollport, so a page change can put the reader back at the top. */
    const scrollRef = useRef<HTMLDivElement | null>(null);

    const load = useCallback(async () => {
        if (!jobId || !client?.data) return;
        setLoading(true);
        setError("");
        try {
            const { rows, truncated: hitCeiling } = await fetchAllApplications(client.data, jobId);
            setTruncated(hitCeiling);

            const parsed: Applicant[] = rows.map(row => {
                // An included relation arrives wrapped: a reference around an
                // Entity around the row. Same shape the jobs list unwraps for
                // the company logo; a bare uuid means nothing included it.
                const ref = row.talent_id;
                const talent = ref && typeof ref === "object"
                    ? (ref.data?.values ?? ref.data ?? ref)
                    : null;

                const summary = summarizeTalent(talent ?? {});
                // Areas, levels and credentials against what the vacancy asks
                // — including which questions this person was ever put. A
                // candidate who predates the skills question has no answer to
                // it, which is not a no.
                const fit = applicantFit(ask, talent ?? {});

                return {
                    applicationId: String(row.id),
                    talentId: talent?.id ? String(talent.id) : "",
                    fullName: talent?.full_name || talent?.email || "(no encontramos la ficha de esta persona)",
                    photoKey: talent?.photo_url ?? null,
                    email: talent?.email ?? null,
                    linkedinUrl: talent?.linkedin_url ?? null,
                    cvUrl: talent?.cv_url ?? null,
                    status: (row.status ?? "applied") as ApplicationStatus,
                    appliedAt: row.created_at ?? null,
                    summary,
                    matched: fit.matched,
                    missing: fit.missing,
                    depth: fit.depth,
                    heldRequired: fit.required.held,
                    inferredRequired: fit.required.inferred,
                    missingRequired: fit.required.missing,
                    unknownRequired: fit.required.unknown,
                    heldDesired: fit.heldDesired,
                    fit
                };
            });

            /**
             * Sorted once, here, and then left alone.
             *
             * The array `applicants` holds is the display order, and deciding on
             * somebody must not disturb it. `byFit` sinks a settled application,
             * so re-deriving the order after each click would drop the card the
             * recruiter just acted on to the bottom of the list — and on a
             * filtered tab, out of it entirely — while the cursor was still over
             * it. So a status change patches one chip in place (see `setStatus`)
             * and only a reload or the sort chips reorder anything.
             */
            parsed.sort(COMPARATORS[sortRef.current]);

            setApplicants(parsed);
        } catch (err: any) {
            setError(err?.message || "No se pudieron cargar las postulaciones");
        } finally {
            setLoading(false);
        }
        // `ask` belongs here because the overlap is computed while parsing, so
        // editing what the vacancy asks for has to re-parse. It is `useMemo`'d
        // off `entity`, so this does not re-fire on unrelated renders.
    }, [client, jobId, ask]);

    useEffect(() => {
        void load();
    }, [load]);

    /** Advance one application and patch it in place — no reload, no lost scroll. */
    const setStatus = useCallback(async (applicationId: string, status: ApplicationStatus) => {
        if (!client?.data) return;
        setSavingId(applicationId);
        try {
            await (client.data as any).collection("talent-applications").update(applicationId, { status });
            setApplicants(prev => prev?.map(a =>
                a.applicationId === applicationId ? { ...a, status } : a
            ) ?? null);
        } catch (err: any) {
            setError(err?.message || "No se pudo guardar el cambio. Vuelve a intentarlo.");
        } finally {
            setSavingId(null);
        }
    }, [client]);

    /**
     * Re-order what is already on screen.
     *
     * The sort chips reorder an array in memory; they do not go back to the
     * server for it. `load` does its own first sort off `sortRef`, so this fires
     * only when somebody actually changes the order, and on mount over a `null`
     * list, where it does nothing.
     */
    useEffect(() => {
        setApplicants(prev => (prev ? [...prev].sort(COMPARATORS[sortMode]) : prev));
    }, [sortMode]);

    // ── What the toolbar leaves on screen ────────────────────────────
    //
    // Search and the requirements gate are applied first, and the tabs last,
    // so the count on a tab is "how many of what you are looking at are in this
    // state" rather than a number that ignores the search box above it.

    const found = useMemo(() => {
        if (!applicants) return [];
        const term = search.trim().toLowerCase();
        return applicants.filter(a => {
            if (onlyQualified && a.missingRequired.length > 0) return false;
            if (!term) return true;
            return a.fullName.toLowerCase().includes(term)
                || (a.email ?? "").toLowerCase().includes(term);
        });
    }, [applicants, search, onlyQualified]);

    const counts = useMemo(() => {
        const out: Record<StatusFilter, number> = {
            all: found.length, applied: 0, reviewing: 0, interview: 0, hired: 0, rejected: 0
        };
        for (const a of found) out[a.status] += 1;
        return out;
    }, [found]);

    const visible = useMemo(
        () => (tab === "all" ? found : found.filter(a => a.status === tab)),
        [found, tab]
    );

    /** Applicants the vacancy's own requirements rule out. */
    const shortCount = useMemo(
        () => (applicants ?? []).filter(a => a.missingRequired.length > 0).length,
        [applicants]
    );

    const pageCount = Math.max(1, Math.ceil(visible.length / PER_PAGE));
    // Clamped rather than corrected in an effect: narrowing six pages to one
    // must not render an empty page for one frame on the way to fixing itself.
    const safePage = Math.min(page, pageCount - 1);
    const pageRows = useMemo(
        () => visible.slice(safePage * PER_PAGE, safePage * PER_PAGE + PER_PAGE),
        [visible, safePage]
    );

    /** Whether anything is narrowing the list — what "quitar filtros" undoes. */
    const narrowed = tab !== "all" || search.trim() !== "" || onlyQualified;

    const clearFilters = useCallback(() => {
        setTab("all");
        setSearch("");
        setOnlyQualified(false);
        setSearchKey(k => k + 1);
    }, []);

    // Back to the first page whenever the set under the pager changes. Without
    // it, narrowing from six pages to one leaves the reader on page four of one.
    useEffect(() => { setPage(0); }, [tab, search, onlyQualified, sortMode]);

    /**
     * Paging puts you back at the top.
     *
     * The pager is at the bottom, which is where you are when you press it, so
     * without this "siguientes" lands on the last card of the next page and
     * reads as nothing having happened.
     */
    const goToPage = useCallback((next: number) => {
        setPage(next);
        scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    if (!jobId) {
        if (formContext?.status === "new") {
            return (
                <div className="p-8">
                    <Alert color="info">Guarda la vacante para ver sus postulaciones.</Alert>
                </div>
            );
        }
        return <CircularProgressCenter />;
    }

    if (!applicants && !error) return <CircularProgressCenter />;

    return (
        <div ref={scrollRef} className="flex flex-col h-full w-full overflow-y-auto">
            {liveRegion}
            <div className="max-w-4xl mx-auto w-full p-6 flex flex-col gap-4">

                <div className="flex items-start gap-3">
                    <div className="grow">
                        <Typography variant="subtitle1" component="h2">
                            {applicants?.length
                                ? `${applicants.length} ${applicants.length === 1 ? "postulación" : "postulaciones"}`
                                : "Postulaciones"}
                        </Typography>
                        <Typography variant="body2" color="secondary">
                            {jobAreas.length
                                ? `Comparadas con lo que pide la vacante: ${jobAreas.map(k => AREA_LABELS[k] ?? k).join(", ")}.`
                                : "Esta vacante no declara áreas de sostenibilidad, así que no hay nada con que comparar."}
                        </Typography>
                    </div>
                    <Tooltip title="Recargar" asChild>
                        <IconButton size="small" aria-label="Volver a mirar las postulaciones" onClick={load} disabled={loading} aria-busy={loading}>
                            <RefreshCwIcon size={iconSize.small} className={cls(loading && "animate-spin")} />
                        </IconButton>
                    </Tooltip>
                </div>

                {error && <Alert color="error">{error}</Alert>}

                {/* Everything below is derived over "all of them", so when the
                    walk stopped short the panel says so rather than presenting
                    a slice as the whole. */}
                {truncated && (
                    <Alert color="warning">
                        Esta vacante tiene más de {MAX_APPLICANTS.toLocaleString("es")} postulaciones. Estás viendo
                        las {MAX_APPLICANTS.toLocaleString("es")} más recientes, y el orden por encaje se ha calculado
                        sólo sobre ésas. Busca por nombre o filtra por estado para trabajar sobre un grupo concreto.
                    </Alert>
                )}

                {jobUsesCatchAll && jobAreas.length === 0 && (
                    <Alert color="warning">
                        La vacante sólo marca «Otra área», que cumple todo el mundo. Elige áreas concretas en la vacante para que esta comparación diga algo. Elige áreas
                        concretas para que esta comparación diga algo.
                    </Alert>
                )}

                {applicants?.length === 0 && (
                    <div className={cls("border rounded-lg px-4 py-8 text-center", defaultBorderMixin)}>
                        <Typography variant="body2" color="secondary">
                            Nadie se ha postulado a esta vacante todavía.
                        </Typography>
                    </div>
                )}

                {/*
                    ── The toolbar ──────────────────────────────────────

                    Every control here appears exactly when it can change what
                    is on screen, and not before. A search box over eight cards
                    that are all already visible, a row of state chips when every
                    application is in the same state, a requirements filter on a
                    vacancy that requires nothing — each of those is a control
                    that answers a question nobody has yet. The panel already
                    refuses to draw the areas row on a vacancy with no areas;
                    this is that same rule applied to the filters.
                */}
                {applicants && applicants.length > 0 && (
                    <div className="flex flex-col gap-2">

                        {applicants.length > SEARCHABLE_FROM && (
                            <SearchBar
                                key={searchKey}
                                size="small"
                                placeholder="Buscar por nombre o correo"
                                onTextSearch={(term: string | undefined) => setSearch(term ?? "")}
                            />
                        )}

                        {/*
                            All six, always, including the ones holding nobody.
                            They were drawn only when they had something in them
                            and that hid the feature: a vacancy whose applications
                            are all in one state showed one chip, so the person
                            looking for "las descartadas" found no evidence that
                            filtering by state was a thing this panel did. A tab
                            reading "· 0" answers the question it was hiding.
                        */}
                        <div className="flex flex-wrap gap-1.5">
                            {STATUS_TABS.map(t => (
                                <FilterChip key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
                                    {t.label} · {counts[t.id]}
                                </FilterChip>
                            ))}
                        </div>

                        {/* Only when the vacancy demands something *and* somebody
                            falls short of it. A gate that excludes nobody is a
                            switch with one position. */}
                        {required.length > 0 && shortCount > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                <FilterChip active={onlyQualified} onClick={() => setOnlyQualified(v => !v)}>
                                    {onlyQualified
                                        ? `Sólo quien cumple los requisitos · ${shortCount} fuera`
                                        : "Sólo quien cumple los requisitos"}
                                </FilterChip>
                            </div>
                        )}

                        {applicants.length > 1 && (
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <Typography variant="body2" color="secondary">Ordenar por</Typography>
                                {(Object.keys(SORT_LABEL) as SortMode[]).map(mode => (
                                    <Tooltip key={mode} title={mode === "fit" ? FIT_RULES : SORT_HINT[mode]} asChild>
                                        <FilterChip active={sortMode === mode} onClick={() => setSortMode(mode)}>
                                            {SORT_LABEL[mode]}
                                        </FilterChip>
                                    </Tooltip>
                                ))}
                            </div>
                        )}

                        {/* What the open tab holds and what the order did, in
                            the place where somebody would be asking. */}
                        <Typography variant="body2" color="secondary">
                            {STATUS_TAB_HINT[tab]}
                        </Typography>
                        {applicants.length > 1 && (
                            <Typography variant="body2" color="secondary">
                                <span className="opacity-70">Orden por {SORT_LABEL[sortMode]}: </span>
                                {SORT_HINT[sortMode]}
                            </Typography>
                        )}
                    </div>
                )}

                {/* Narrowed to nothing — which is a different thing from an
                    empty vacancy, and says so, with the way back. */}
                {applicants && applicants.length > 0 && visible.length === 0 && (
                    <div className={cls("border rounded-lg px-4 py-8 text-center flex flex-col items-center gap-2", defaultBorderMixin)}>
                        <Typography variant="body2" color="secondary">
                            Ninguna postulación cumple lo que has filtrado.
                        </Typography>
                        <Button size="small" variant="text" onClick={clearFilters}>
                            Quitar los filtros
                        </Button>
                    </div>
                )}

                {pageRows.map(applicant => {
                    const busy = savingId === applicant.applicationId;
                    const chip = STATUS_CHIP[applicant.status];
                    return (
                        <Card key={applicant.applicationId} className={cls("p-4 flex flex-col gap-3", cardMixin)}>

                            <div className="flex items-start gap-3">
                                {/* The face of whoever applied, before the
                                    reasons they might fit. A vacancy's
                                    applicants are read one after another and
                                    compared, and a column of names gives that
                                    comparison nothing to hold on to. */}
                                <PersonAvatar
                                    name={applicant.fullName}
                                    photoKey={applicant.photoKey}
                                    size={40}
                                />
                                <div className="grow min-w-0">
                                    {/* The name is the obvious thing to click to
                                        read someone, so it opens the profile —
                                        the ↗ button on the right does the same
                                        and stays for discoverability. Rendered
                                        as a plain heading when there is no
                                        talent row behind it, rather than as a
                                        control that would do nothing.

                                        The button goes *inside* the heading and
                                        not around it: a heading is flow content
                                        and a button only accepts phrasing, so
                                        the other nesting is invalid HTML — and
                                        it does not merely warn, it swallows
                                        pointer events. The handler fired on a
                                        programmatic `.click()` and on nothing a
                                        mouse could do. */}
                                    <Typography variant="subtitle2" component="h3">
                                        {applicant.talentId ? (
                                            <button
                                                type="button"
                                                onClick={() => sidePanel.open({ path: "talents", entityId: applicant.talentId })}
                                                // Padding in, margin back out: a
                                                // 20px line of text is a mean
                                                // target, and this widens the
                                                // hit area without moving
                                                // anything on the card.
                                                className="text-left hover:underline py-1 -my-1"
                                            >
                                                {applicant.fullName}
                                            </button>
                                        ) : applicant.fullName}
                                    </Typography>
                                    <Typography variant="body2" color="secondary">
                                        {[
                                            applicant.summary.location,
                                            applicant.summary.experienceLabel,
                                            formatDate(applicant.appliedAt)
                                                ? `postuló el ${formatDate(applicant.appliedAt)}`
                                                : null
                                        ].filter(Boolean).join(" · ")}
                                    </Typography>
                                </div>
                                <Chip size="small" colorScheme={chip.color}>{chip.label}</Chip>
                            </div>

                            {/* What the vacancy asks for, and what this person claims about
                                it. Colour is the level claimed, not a verdict. */}
                            {jobAreas.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {applicant.matched.map(area => (
                                        <Chip key={area.key} size="smallest" colorScheme={LEVEL_COLORS[area.level as keyof typeof LEVEL_COLORS]}>
                                            {area.label} · {area.levelLabel}
                                        </Chip>
                                    ))}
                                    {applicant.missing.map(area => (
                                        <Tooltip key={area.key} title="No declara experiencia en esta área" asChild>
                                            <span className="text-xs px-2 py-0.5 rounded-full border border-dashed border-hairline-strong text-text-secondary dark:text-text-secondary-dark">
                                                {area.label}
                                            </span>
                                        </Tooltip>
                                    ))}
                                </div>
                            )}

                            {/* The credentials the vacancy named. Same idiom as the areas
                                above: an intersection printed as chips, no score. A held
                                requirement is green, a missing one is called out in red —
                                that one is the panel's only hard verdict, and it is the
                                vacancy's own, not ours. */}
                            {(required.length > 0 || desired.length > 0) && (
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {applicant.heldRequired.map(id => (
                                        <Chip key={id} size="smallest" colorScheme="greenLighter">
                                            {credentialLabel(id)}
                                        </Chip>
                                    ))}
                                    {/* Green like a declared one because the answer to
                                        "can they do this" is yes — with a dashed
                                        edge and a tooltip naming the source,
                                        because the answer to "who says so" is us.
                                        Nobody was asked the skills question until
                                        after they had filled the form. */}
                                    {applicant.inferredRequired.map(id => (
                                        <Tooltip key={id} title="Deducida de lo que escribió en su perfil (estudios, trayectoria, certificaciones). No la declaró: nunca se le preguntó." asChild>
                                            <span className="text-xs px-2 py-0.5 rounded-full border border-dashed border-green-400 dark:border-green-700 text-green-700 dark:text-green-400">
                                                {credentialLabel(id)} ·  deducida
                                            </span>
                                        </Tooltip>
                                    ))}
                                    {applicant.missingRequired.map(id => (
                                        <Tooltip key={id} title="La vacante la exige, y esta persona respondió a esa pregunta sin incluirla" asChild>
                                            <span className="text-xs px-2 py-0.5 rounded-full border border-dashed border-red-300 dark:border-red-800 text-red-500 dark:text-red-400">
                                                falta: {credentialLabel(id)}
                                            </span>
                                        </Tooltip>
                                    ))}
                                    {/* Grey, not red, and worded as a gap in what we know
                                        rather than in the person. Every candidate in the
                                        database predates the questionnaire's skills
                                        question, so this is currently the common case and
                                        showing it as "falta" was simply false. */}
                                    {applicant.unknownRequired.map(id => (
                                        <Tooltip key={id} title="Nunca se le preguntó: el cuestionario que rellenó no incluía esta pregunta. No es lo mismo que no tenerla." asChild>
                                            <span className="text-xs px-2 py-0.5 rounded-full border border-dashed border-hairline-strong text-text-secondary dark:text-text-secondary-dark">
                                                nunca se lo preguntamos: {credentialLabel(id)}
                                            </span>
                                        </Tooltip>
                                    ))}
                                    {applicant.heldDesired.map(id => (
                                        <Tooltip key={id} title="Deseable en esta vacante" asChild>
                                            <Chip size="smallest" colorScheme="blueLighter">
                                                + {credentialLabel(id)}
                                            </Chip>
                                        </Tooltip>
                                    ))}
                                </div>
                            )}

                            {/* Caveats, only when there is one. Both are reasons to read the
                                claims above more sceptically, so they sit under them. */}
                            {(applicant.summary.inflated || !applicant.summary.completed) && (
                                <div className="flex flex-wrap gap-3">
                                    {applicant.summary.inflated && (
                                        <Typography variant="body2" className="text-amber-600 dark:text-amber-400">
                                            ⚠ Se declara avanzado en casi todas las áreas
                                        </Typography>
                                    )}
                                    {!applicant.summary.completed && (
                                        <Typography variant="body2" color="secondary">
                                            Cuestionario sin terminar
                                        </Typography>
                                    )}
                                </div>
                            )}

                            {/* The verb, once, in front of the row — so the
                                buttons can be the state names, matching the chip
                                above them and the tabs above those. */}
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <Typography variant="body2" color="secondary" className="mr-0.5">
                                    Mover a
                                </Typography>
                                {STATUS_ACTIONS.map(next => {
                                    const here = applicant.status === next;
                                    return (
                                        <Tooltip
                                            key={next}
                                            title={here ? "Es donde está ahora" : STATUS_MOVE_HINT[next]}
                                            asChild
                                        >
                                            {/* The tooltip needs an element that
                                                still emits pointer events, and a
                                                disabled button does not — hence
                                                the span, which also gives the
                                                current state a hoverable label
                                                rather than a dead word. */}
                                            <span className="inline-flex">
                                                <button
                                                    type="button"
                                                    disabled={busy || here}
                                                    aria-current={here ? "true" : undefined}
                                                    onClick={() => setStatus(applicant.applicationId, next)}
                                                    className={cls(
                                                        "text-xs px-2.5 py-1 rounded-md border transition-colors",
                                                        defaultBorderMixin,
                                                        here
                                                            ? "opacity-40 cursor-default"
                                                            : surfaceHoverMixin
                                                    )}
                                                >
                                                    {STATUS_CHIP[next].label}
                                                </button>
                                            </span>
                                        </Tooltip>
                                    );
                                })}

                                <div className="flex items-center gap-1 ml-auto">
                                    {applicant.talentId && (
                                        <Tooltip title="Ver perfil completo" asChild>
                                            <IconButton
                                                size="small"
                                                aria-label="Ver perfil"
                                                className="text-text-secondary dark:text-text-secondary-dark"
                                                onClick={() => sidePanel.open({ path: "talents", entityId: applicant.talentId })}
                                            >
                                                <ExternalLinkIcon size={iconSize.smallest} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {applicant.linkedinUrl && (
                                        <Tooltip title="LinkedIn" asChild>
                                            <IconButton
                                                size="small"
                                                aria-label="LinkedIn"
                                                className="text-text-secondary dark:text-text-secondary-dark"
                                                component="a"
                                                href={applicant.linkedinUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <Link2Icon size={iconSize.smallest} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {applicant.cvUrl && (
                                        <Tooltip title="Abrir el CV" asChild>
                                            <IconButton
                                                size="small"
                                                aria-label="Abrir el CV"
                                                className="text-text-secondary dark:text-text-secondary-dark"
                                                onClick={() => void openCv(applicant.cvUrl!)}
                                            >
                                                <FileTextIcon size={iconSize.smallest} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        </Card>
                    );
                })}

                {/*
                    ── The pager ────────────────────────────────────────

                    Drawn only when there is a second page. The count is the
                    honest one — how many the filters left, not how many the
                    vacancy has — because it labels the thing being paged.
                */}
                {visible.length > PER_PAGE && (
                    <div className={cls("flex items-center justify-between gap-3 flex-wrap pt-2 border-t", defaultBorderMixin)}>
                        <Typography variant="body2" color="secondary">
                            {safePage * PER_PAGE + 1}–{Math.min((safePage + 1) * PER_PAGE, visible.length)} de {visible.length}
                            {narrowed && applicants ? ` (de ${applicants.length} en total)` : ""}
                        </Typography>
                        <div className="flex items-center gap-1">
                            <Button
                                size="small"
                                variant="text"
                                disabled={safePage === 0}
                                onClick={() => goToPage(safePage - 1)}
                            >
                                <ChevronLeftIcon size={iconSize.smallest} /> Anteriores
                            </Button>
                            <Typography variant="body2" color="secondary" className="px-1 tabular-nums">
                                {safePage + 1} / {pageCount}
                            </Typography>
                            <Button
                                size="small"
                                variant="text"
                                disabled={safePage >= pageCount - 1}
                                onClick={() => goToPage(safePage + 1)}
                            >
                                Siguientes <ChevronRightIcon size={iconSize.smallest} />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
